/**
 * Monta markdown hierárquico no painel Orya (equipamento + filhos abaixo).
 */

import {
  getPecasDetailInlineFieldIds,
  getPecasDetailRowValue,
  hasPecasDetailRowsEnabled,
  rowHasPecasDetailLines,
  PECAS_LINHAS_ROW_KEY,
  sortPecasDetailLinesByQtdeDesc,
} from './relatoriosPecas';
import { syncIntroWithEquipBlockCount } from './relatoriosOryaTableAssert';

const CHILD_BLOCK_FIELDS = [
  { id: 'pecaReferencia', header: 'Referência' },
  { id: 'pecaNome', header: 'Peça' },
  { id: 'pecaCod', header: 'Cód. Peça' },
  { id: 'pecaQtde', header: 'Qtde' },
  { id: 'pecaMarca', header: 'Marca' },
  { id: 'pecaVidaUtil', header: 'Vida útil' },
  { id: 'pecaCustoUnitario', header: 'Custo' },
];

function normMatchKey(value) {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');
}

function escapeMdCell(value) {
  return String(value ?? '')
    .replace(/\|/g, '\\|')
    .replace(/\r?\n/g, ' ')
    .trim();
}

function headerIndex(headers, pattern) {
  return (headers || []).findIndex((h) => pattern.test(String(h || '')));
}

function tableToMarkdown(headers, rows) {
  if (!headers?.length) return '';
  const sep = headers.map(() => '---');
  const body = (rows || []).map((r) =>
    headers.map((_, i) => escapeMdCell(r[i])).join(' | ')
  );
  return [
    `| ${headers.join(' | ')} |`,
    `| ${sep.join(' | ')} |`,
    ...body.map((line) => `| ${line} |`),
  ].join('\n');
}

function buildChildColumns(effectiveColumnRefs) {
  const enabled = new Set(getPecasDetailInlineFieldIds(effectiveColumnRefs || []));
  return CHILD_BLOCK_FIELDS.filter((f) => enabled.has(f.id));
}

function pecaLineToCells(pecaLine, columns) {
  return columns.map((col) => {
    const v = getPecasDetailRowValue(pecaLine, col.id);
    return v != null && String(v).trim() !== '' ? String(v).trim() : '-';
  });
}

/** Mapa `serie:XXX` / `resumo:YYY` → linhas de peça formatadas. */
export function buildOryaPecasLookup(rows, effectiveColumnRefs) {
  const lookup = new Map();
  if (!hasPecasDetailRowsEnabled(effectiveColumnRefs)) return lookup;

  for (const row of rows || []) {
    if (!rowHasPecasDetailLines(row)) continue;
    const lines = sortPecasDetailLinesByQtdeDesc(
      row[PECAS_LINHAS_ROW_KEY] || row.__pecasLinhas
    );
    if (!lines.length) continue;

    const serie = normMatchKey(row.serie);
    const resumo = normMatchKey(row.resumoEquipamento || row.equipamentoResumo);
    if (serie) lookup.set(`serie:${serie}`, lines);
    if (resumo) lookup.set(`resumo:${resumo}`, lines);
  }
  return lookup;
}

export function findPecasLinesForTableRow(tableRow, headers, pecasLookup) {
  if (!pecasLookup?.size || !tableRow?.length) return null;
  const serieIdx = headerIndex(headers, /série|serie/i);
  const resumoIdx = headerIndex(headers, /resumo\s*equip/i);

  if (serieIdx >= 0) {
    const hit = pecasLookup.get(`serie:${normMatchKey(tableRow[serieIdx])}`);
    if (hit?.length) return hit;
  }
  if (resumoIdx >= 0) {
    const hit = pecasLookup.get(`resumo:${normMatchKey(tableRow[resumoIdx])}`);
    if (hit?.length) return hit;
  }
  return null;
}

/** Pedido explícito de filhos abaixo do pai (peças, referência, etc.). */
export function userRequestsNestedChildDetail(text) {
  const t = String(text || '').toLowerCase();
  return (
    /abaixo\s+(dos?\s+)?equip/.test(t) ||
    /mostre.*peças/.test(t) ||
    /peças.*(referência|nome)/.test(t) ||
    /(referência|nome).*peças/.test(t) ||
    /listar.*peças/.test(t) ||
    /detalhe.*peças/.test(t) ||
    /peças\s+trocadas/.test(t) ||
    /com\s+referência\s+e\s+nome/.test(t)
  );
}

/** Usuário pediu tabela-resumo do equipamento (OS, produção, uptime, etc.). */
export function userRequestsEquipmentSummaryTable(text) {
  const t = String(text || '').toLowerCase();
  return (
    /tabela\s+(de\s+)?equipamentos?/.test(t) ||
    /mostre\s+(a\s+)?tabela/.test(t) ||
    /com\s+(os|uptime|produção|prod\.|métricas?|indicadores?)/.test(t) ||
    /inclu(a|ir)\s+(os|uptime|produção)/.test(t) ||
    /listar\s+equipamentos?\s+com\s+(os|uptime|produção)/.test(t) ||
    /resumo\s+do\s+equipamento\s+com/.test(t)
  );
}

/** Padrão peças abaixo: título do equipamento + tabela de peças (sem tabela-resumo duplicada). */
export function shouldUsePartsFocusedLayout(lastUserMessage) {
  if (userRequestsEquipmentSummaryTable(lastUserMessage)) return false;
  return userRequestsNestedChildDetail(lastUserMessage);
}

export function shouldEnrichPanelWithChildBlocks({ lastUserMessage, intro }) {
  if (userRequestsNestedChildDetail(lastUserMessage)) return true;
  const introText = String(intro || '');
  return /equipamento.*peças|peças.*abaixo|confira.*peças|e\s+suas\s+peças/i.test(introText);
}

/** Remove do intro listas de peças / títulos de filho antes do equipamento (pai). */
export function stripPrematureChildDetailFromIntro(text) {
  const lines = String(text || '').split(/\r?\n/);
  const out = [];
  let skipChildBlock = false;

  for (const line of lines) {
    const t = line.trim();
    if (!t) {
      if (!skipChildBlock) out.push(line);
      continue;
    }
    if (
      /^\*\*peças/i.test(t) ||
      /peças\s+trocadas\s+(neste|no|do)/i.test(t) ||
      /^#{1,4}\s*peças/i.test(t)
    ) {
      skipChildBlock = true;
      continue;
    }
    if (skipChildBlock && (t.startsWith('|') || /^#{1,4}\s/.test(t) || /^a\)\s/i.test(t))) {
      skipChildBlock = false;
    }
    if (skipChildBlock) continue;
    if (/^[-*•]\s+/.test(t) && /(qtde|quantidade|referência|·\s*qtde)/i.test(t)) continue;
    if (/confira.*peças|equipamento\s+e\s+suas\s+peças/i.test(t)) continue;
    out.push(line);
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** Intro curto: critério + quantidade; sem antecipar filhos. equipCount = blocos exibidos no painel. */
export function buildHierarchicalPanelIntro(intro, equipCount) {
  const base = stripPrematureChildDetailFromIntro(intro);
  const n = Math.max(0, Number(equipCount) || 0);
  if (!base) {
    return n === 1
      ? 'Segue 1 equipamento que atende ao critério solicitado:'
      : `Seguem os ${n} equipamentos que atendem ao critério solicitado:`;
  }
  return syncIntroWithEquipBlockCount(base, n);
}

/** Mantém só o texto de critério (remove tabelas e blocos equipamento/peças gerados pelo modelo). */
export function extractIntroOnlyProse(text) {
  const lines = String(text || '').split(/\r?\n/);
  const out = [];
  for (const line of lines) {
    const t = line.trim();
    if (!t) {
      if (out.length && out[out.length - 1] !== '') out.push('');
      continue;
    }
    if (t.startsWith('|')) break;
    if (/^#{1,4}\s/.test(t)) continue;
    if (/^---+$/.test(t)) continue;
    if (/^\*\*equipamento/i.test(t)) continue;
    if (/^\*\*peças/i.test(t)) continue;
    if (/^detalhe\s+das\s+peças/i.test(t)) continue;
    out.push(line);
  }
  return stripPrematureChildDetailFromIntro(out.join('\n').replace(/\n{3,}/g, '\n\n').trim());
}

function buildEquipBlockTitle(headers, row) {
  const serieIdx = headerIndex(headers, /série|serie/i);
  const resumoIdx = headerIndex(headers, /resumo\s*equip/i);
  const eqIdx = (headers || []).findIndex(
    (h) => /equipamento/i.test(String(h)) && !/resumo/i.test(String(h))
  );
  let name = '';
  if (resumoIdx >= 0) name = String(row[resumoIdx] ?? '').trim();
  else if (eqIdx >= 0) name = String(row[eqIdx] ?? '').trim();
  const serie = serieIdx >= 0 ? String(row[serieIdx] ?? '').trim() : '';
  if (name && serie) {
    return `Equipamento: ${escapeMdCell(name)} (Série: ${escapeMdCell(serie)})`;
  }
  if (serie) return `Equipamento (Série: ${escapeMdCell(serie)})`;
  return 'Equipamento';
}

function equipTitleFromReportRow(row) {
  const name = String(
    row?.resumoEquipamento ?? row?.equipamentoResumo ?? row?.equipamento ?? ''
  ).trim();
  const serie = String(row?.serie ?? '').trim();
  if (name && serie) {
    return `Equipamento: ${escapeMdCell(name)} (Série: ${escapeMdCell(serie)})`;
  }
  if (serie) return `Equipamento (Série: ${escapeMdCell(serie)})`;
  return 'Equipamento';
}

export function shouldFilterPecasQtyGreaterThanOne(text) {
  const t = String(text || '').toLowerCase();
  return (
    /quantidade\s*>\s*1/.test(t) ||
    /qtdade\s*>\s*1/.test(t) ||
    /qtde\s*>\s*1/.test(t) ||
    /mais de uma vez/.test(t) ||
    /mesmo\s+c[oó]digo/.test(t) ||
    /superior a\s*1[^0-9]/.test(t)
  );
}

function filterPecaLinesByMinQty(lines, minExclusive = 1) {
  if (!Array.isArray(lines)) return [];
  return lines.filter((line) => (Number(line?.qtde) || 0) > minExclusive);
}

function pecaLinesForReportRow(row, pecasLookup, lastUserMessage = '') {
  const serie = normMatchKey(row?.serie);
  const resumo = normMatchKey(row?.resumoEquipamento || row?.equipamentoResumo);
  let lines = null;
  if (serie && pecasLookup.has(`serie:${serie}`)) lines = pecasLookup.get(`serie:${serie}`);
  else if (resumo && pecasLookup.has(`resumo:${resumo}`)) lines = pecasLookup.get(`resumo:${resumo}`);
  else {
    const raw = row?.[PECAS_LINHAS_ROW_KEY] || row?.__pecasLinhas;
    lines = Array.isArray(raw) ? raw : null;
  }
  if (shouldFilterPecasQtyGreaterThanOne(lastUserMessage)) {
    return filterPecaLinesByMinQty(lines, 1);
  }
  return lines;
}

function extractSeriesFromParsedRows(headers, rows) {
  const serieIdx = headerIndex(headers, /série|serie/i);
  if (serieIdx < 0) return new Set();
  return new Set(
    (rows || []).map((r) => normMatchKey(r[serieIdx])).filter(Boolean)
  );
}

/**
 * Título por equipamento + tabela de peças (sem tabela-resumo de equipamentos).
 */
function buildPartsFocusedHierarchicalMarkdown(headers, rows, pecasLookup, effectiveColumnRefs) {
  const parts = [];
  let anyChild = false;

  for (const row of rows) {
    const pecaLines = findPecasLinesForTableRow(row, headers, pecasLookup);
    if (!pecaLines?.length) continue;
    const columns = buildChildColumns(effectiveColumnRefs);
    const childHeaders = columns.map((c) => c.header);
    const childRows = pecaLines.map((line) => pecaLineToCells(line, columns));
    if (!childRows.some((r) => r.some((c) => c && c !== '-'))) continue;
    anyChild = true;
    parts.push('', `**${buildEquipBlockTitle(headers, row)}**`, tableToMarkdown(childHeaders, childRows));
  }

  return anyChild ? parts.join('\n').trim() : null;
}

/** Monta blocos a partir das linhas do relatório (ordem da tela / TSV). */
export function buildPartsFocusedFromReportRows(
  sourceRows,
  pecasLookup,
  effectiveColumnRefs,
  { seriesFilter = null, lastUserMessage = '' } = {}
) {
  const columns = buildChildColumns(effectiveColumnRefs);
  if (!columns.length) return null;
  const childHeaders = columns.map((c) => c.header);
  const parts = [];
  let anyChild = false;

  for (const row of sourceRows || []) {
    const serieKey = normMatchKey(row?.serie);
    if (seriesFilter?.size && serieKey && !seriesFilter.has(serieKey)) continue;
    const pecaLines = pecaLinesForReportRow(row, pecasLookup, lastUserMessage);
    if (!pecaLines?.length) continue;
    const childRows = pecaLines.map((line) => pecaLineToCells(line, columns));
    if (!childRows.some((r) => r.some((c) => c && c !== '-'))) continue;
    anyChild = true;
    parts.push(
      '',
      `**${equipTitleFromReportRow(row)}**`,
      tableToMarkdown(childHeaders, childRows)
    );
  }

  return anyChild ? parts.join('\n').trim() : null;
}

export function extractSeriesFromRawText(text) {
  const set = new Set();
  const raw = String(text || '');
  const re = /\(Série:\s*([^)]+)\)/gi;
  let m = re.exec(raw);
  while (m) {
    const k = normMatchKey(m[1]);
    if (k) set.add(k);
    m = re.exec(raw);
  }
  return set;
}

export function resolveEquipRowsForPartsFocus(
  headers,
  parsedRows,
  sourceRows,
  pecasLookup,
  rawContent = ''
) {
  const seriesParsed = extractSeriesFromParsedRows(headers, parsedRows);
  let series = seriesParsed;
  if (!series.size && rawContent) {
    series = extractSeriesFromRawText(rawContent);
  }
  if (series.size > 0 && sourceRows?.length) {
    const matched = sourceRows.filter((r) => series.has(normMatchKey(r.serie)));
    if (matched.length) return matched;
  }
  if (sourceRows?.length) {
    return sourceRows.filter(
      (r) => pecaLinesForReportRow(r, pecasLookup, rawContent)?.length
    );
  }
  return [];
}

function buildChildTableTitle(headers, row, multiEquip) {
  if (!multiEquip) return 'Peças trocadas';
  const serieIdx = headerIndex(headers, /série|serie/i);
  const resumoIdx = headerIndex(headers, /resumo\s*equip/i);
  if (serieIdx >= 0) {
    return `Peças trocadas — série ${escapeMdCell(row[serieIdx])}`;
  }
  if (resumoIdx >= 0) {
    return `Peças trocadas — ${escapeMdCell(row[resumoIdx])}`;
  }
  return 'Peças trocadas';
}

/**
 * Tabela do pai e, abaixo, tabela(s) do filho — nunca colunas mescladas na mesma grade.
 */
function buildStackedHierarchicalMarkdown(headers, rows, pecasLookup, effectiveColumnRefs) {
  const parts = ['**Equipamento**', tableToMarkdown(headers, rows)];
  let anyChild = false;
  const multiEquip = rows.length > 1;

  for (const row of rows) {
    const pecaLines = findPecasLinesForTableRow(row, headers, pecasLookup);
    if (!pecaLines?.length) continue;
    const columns = buildChildColumns(effectiveColumnRefs);
    const childHeaders = columns.map((c) => c.header);
    const childRows = pecaLines.map((line) => pecaLineToCells(line, columns));
    if (!childRows.some((r) => r.some((c) => c && c !== '-'))) continue;
    anyChild = true;
    const title = buildChildTableTitle(headers, row, multiEquip);
    parts.push('', `**${title}**`, tableToMarkdown(childHeaders, childRows));
  }

  return anyChild ? parts.join('\n') : null;
}

/** Pai + filho: layout conforme pedido (peças focadas ou tabela-resumo de equipamentos). */
export function buildHierarchicalPanelMarkdown(
  headers,
  rows,
  pecasLookup,
  effectiveColumnRefs,
  { lastUserMessage = '' } = {}
) {
  if (!headers?.length || !rows?.length || !pecasLookup?.size) return null;

  const serieIdx = headerIndex(headers, /série|serie/i);
  const resumoIdx = headerIndex(headers, /resumo\s*equip/i);
  if (serieIdx < 0 && resumoIdx < 0) return null;

  if (shouldUsePartsFocusedLayout(lastUserMessage)) {
    return null;
  }

  return buildStackedHierarchicalMarkdown(headers, rows, pecasLookup, effectiveColumnRefs);
}

export { countMarkdownTables } from './relatoriosOryaMarkdownParse';

export function getPecasChildFieldLabels(effectiveColumnRefs) {
  return buildChildColumns(effectiveColumnRefs).map((c) => c.header);
}
